import { create } from "zustand";
import { persist } from "zustand/middleware";
import { queryClient } from "@/lib/query-client";
import type { User } from "@/types";

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  hasChecked: boolean;

  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkSession: () => Promise<void>;
  setUser: (user: User | null) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      hasChecked: false,

      login: async (email: string, password: string) => {
        set({ isLoading: true });
        try {
          const res = await fetch("/api/auth/sign-in/email", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ email, password }),
          });
          if (!res.ok) {
            // better-auth returns { message, code } (no "error" wrapper)
            const data = await res.json().catch(() => ({}));
            throw new Error(
              (data as { message?: string; error?: { message?: string } })?.message
                ?? (data as { error?: { message?: string } })?.error?.message
                ?? "Login failed. Please check your credentials and try again."
            );
          }
          // S-080: a new authenticated session starts here — drop any React Query
          // cache left over from a previous user on this tab (covers both the
          // logout path and the session-expiry → re-login path) so the incoming
          // user can never be served the previous user's cached data.
          queryClient.clear();
          // After successful login, check session to get full user object
          const sessionRes = await fetch("/api/auth/get-session", {
            credentials: "include",
          });
          if (sessionRes.ok) {
            const session = await sessionRes.json();
            const baseUser = session.user ?? null;
            // Fetch role from UserProfile — not included in the better-auth session
            let roleName: string | undefined;
            let fillableTags: string[] = ["ALL"];
            let abilities: Array<{ action: string; subject: string }> = [];
            const profileRes = await fetch("/api/v1/users/me", {
              credentials: "include",
            });
            // S-136: a DISABLED account passes better-auth sign-in (which only
            // checks the password) but the app rejects every authenticated
            // request with 403. Detect that here and fail the login cleanly with
            // a clear message, instead of stranding the user in a 403-only app.
            if (profileRes.status === 403) {
              await fetch("/api/auth/sign-out", { method: "POST", credentials: "include" });
              set({ user: null, isAuthenticated: false, isLoading: false, hasChecked: false });
              throw new Error("This account has been disabled. Contact your administrator.");
            }
            try {
              if (profileRes.ok) {
                const profile = await profileRes.json();
                const role = profile.data?.role;
                roleName = typeof role === "object" && role !== null
                  ? role.name
                  : typeof role === "string"
                  ? role
                  : undefined;
                fillableTags = profile.data?.fillableDomainTags ?? ["ALL"];
                abilities = profile.data?.abilities ?? [];
              }
            } catch {
              // Non-fatal — role will be undefined; UI gates degrade gracefully
            }
            set({
              user: baseUser
                ? { ...baseUser, role: roleName ?? "", fillableDomainTags: fillableTags, abilities }
                : null,
              isAuthenticated: true,
              isLoading: false,
            });
          } else {
            set({ isAuthenticated: true, isLoading: false });
          }
        } catch (err) {
          set({ isLoading: false });
          throw err;
        }
      },

      logout: async () => {
        set({ isLoading: true });
        try {
          await fetch("/api/auth/sign-out", {
            method: "POST",
            credentials: "include",
          });
        } finally {
          // S-080: drop all cached queries so the next user on this tab can't be
          // served the previous user's data (React Query would otherwise return it
          // within the 5-min staleTime before any refetch).
          queryClient.clear();
          // Reset hasChecked so a subsequent checkSession() runs on the next page load.
          set({ user: null, isAuthenticated: false, isLoading: false, hasChecked: false });
        }
      },

      checkSession: async () => {
        // Prevent duplicate concurrent or repeat calls (React strict mode fires effects twice)
        if (get().isLoading || get().hasChecked) return;

        set({ isLoading: true });
        try {
          const res = await fetch("/api/auth/get-session", {
            credentials: "include",
          });
          if (res.ok) {
            const data = await res.json();
            if (data?.session && data?.user) {
              // Fetch role from UserProfile — not included in the better-auth session
              let roleName: string | undefined;
              let fillableTags: string[] = ["ALL"];
              let abilities: Array<{ action: string; subject: string }> = [];
              try {
                const profileRes = await fetch("/api/v1/users/me", {
                  credentials: "include",
                });
                if (profileRes.ok) {
                  const profile = await profileRes.json();
                  const role = profile.data?.role;
                  roleName = typeof role === "object" && role !== null
                    ? role.name
                    : typeof role === "string"
                    ? role
                    : undefined;
                  fillableTags = profile.data?.fillableDomainTags ?? ["ALL"];
                  abilities = profile.data?.abilities ?? [];
                }
              } catch {
                // Non-fatal
              }
              set({
                user: { ...data.user, role: roleName ?? "", fillableDomainTags: fillableTags, abilities },
                isAuthenticated: true,
                isLoading: false,
                hasChecked: true,
              });
            } else {
              set({ user: null, isAuthenticated: false, isLoading: false, hasChecked: true });
            }
          } else if (res.status === 401 || res.status === 403) {
            // Only clear auth on actual auth failures
            set({ user: null, isAuthenticated: false, isLoading: false, hasChecked: true });
          } else {
            // 429, 500, network hiccup — keep current auth state, just mark checked
            set({ isLoading: false, hasChecked: true });
          }
        } catch {
          // Network error — keep current auth state, just stop loading
          set({ isLoading: false, hasChecked: true });
        }
      },

      setUser: (user) => set({ user, isAuthenticated: user !== null }),
    }),
    {
      name: "kiborg-auth",
      // Only persist user state, not loading or action functions
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
